import { useRef, useState } from 'react';
import { announceBulkRelease } from '@/stores/releaseUndo.store';
import { announceReleaseMoment } from '@/stores/releaseMoment.store';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHashtag } from '@fortawesome/free-solid-svg-icons';
import { usePostsStore } from '@/stores/postsStore';
import { useInvalidateRelease } from '@/lib/invalidateRelease';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { postsApi } from '@/services/posts';
import { approvalService } from '@/services/approval.service';
import { PostTable } from './PostTable';
import { LabelInput } from './LabelInput';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Icon } from '@/components/ui/Icon';
import { DropdownMenu } from '@/components/ui/DropdownMenu';
import { ICONS } from '@/constants/icons';
import { ConfirmDialog, ConfirmDialogSimple } from '@/components/ui/ConfirmDialog';
import { useExportVariationsTemplate } from '@/hooks/useBulkImport';
import { HashtagGenerator } from '@/components/composer/HashtagGenerator';
import { VariationCreator } from '@/components/VariationCreator';
import { devLog } from '@/utils/logger';
import { toast } from '@/utils/toast';
import { BulkSkippedDialog } from '@/components/release/BulkSkippedDialog';
import { buildBulkSummary, skippedItems } from '@/utils/bulkReleaseSummary';
import type { BulkItemResult, BulkSendResult } from '@/types/approval';

/**
 * PostList Component
 *
 * Main post list with:
 * - Header with select all and bulk actions
 * - PostTable (always table view)
 * - Loading state (skeleton)
 * - Empty state
 * - Pagination controls
 */

interface PostListProps {
  onEdit?: (postId: number) => void;
  onSendForReview?: (postId: number) => void;
  onScheduleNow?: (postId: number) => void;
  workflowEnabled?: boolean;
}

export const PostList = ({ onEdit, onSendForReview, onScheduleNow, workflowEnabled = false }: PostListProps) => {
  const { t } = useTranslation('common');
  const invalidateRelease = useInvalidateRelease();
  const {
    posts,
    selectedPosts,
    isLoading,
    error,
    pagination,
    hasMore,
    circuitBreakerOpen,
    retryFetch,
    selectPost,
    selectAllPosts,
    clearSelection,
    duplicatePost,
    deletePost,
    bulkDuplicate,
    bulkAddLabels,
    bulkAddHashtags,
    exportPosts,
    loadMore,
    clearError,
    setFilter,
  } = usePostsStore();

  const { isAgencyTeam, carteBlanche, canCreateVariation } = useReviewerRestrictions();

  // Per-post permission checks (release rulebook mirror) + the viewer's release capabilities
  const permissions = usePostPermissions();
  // Creators (staff in God Mode and direct non-reviewer members) send content for review
  const canSendForReview = permissions.isCreator && isAgencyTeam;
  // Reviewers release it: direct REVIEWER members AND staff with the reviewer flag (also in God Mode)
  const canReleasePosts = permissions.isReleaseReviewer;
  // Bulk-bar actions mirror the server rules: with the release workflow on, a reviewer-only person may
  // not duplicate, and may edit/delete only their own untouched seed drafts (per-post policy). Workflow
  // off keeps everything (permissions.canXPost returns true there).
  const canBulkDuplicate = !permissions.workflowEnabled || permissions.isCreator;
  const canBulkDelete = posts.some((p) => selectedPosts.includes(p.id) && permissions.canDeletePost(p));
  // A bulk content edit is offered only when every selected post passes the same
  // rule as its single-post Edit action. Missing rows stay unavailable until loaded.
  const canBulkEdit = selectedPosts.length > 0 && selectedPosts.every((id) => {
    const post = posts.find((candidate) => candidate.id === id);
    return post !== undefined && permissions.canEditPost(post);
  });

  const [actionLoading, setActionLoading] = useState(false);
  const [skippedDetails, setSkippedDetails] = useState<BulkItemResult[] | null>(null);

  // Dialog states
  const [showApprovalInfoDialog, setShowApprovalInfoDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showDateRequiredDialog, setShowDateRequiredDialog] = useState(false);
  const [showVersionErrorDialog, setShowVersionErrorDialog] = useState(false);
  const [showBulkLabelsModal, setShowBulkLabelsModal] = useState(false);
  const [bulkLabels, setBulkLabels] = useState<string[]>([]);
  const [showBulkHashtagsModal, setShowBulkHashtagsModal] = useState(false);
  const [bulkHashtags, setBulkHashtags] = useState<string[]>([]);
  const [showHashtagGenerator, setShowHashtagGenerator] = useState(false);

  // Bulk variation flow: open VariationCreator one post at a time, in sequence.
  // Key bumps on each advance to force a fresh modal mount (resets composer state).
  const [bulkVariationQueue, setBulkVariationQueue] = useState<number[]>([]);
  const [bulkVariationCurrentId, setBulkVariationCurrentId] = useState<number | null>(null);
  const [bulkVariationKey, setBulkVariationKey] = useState(0);
  // Tracks whether the imminent onClose is part of an auto-advance (skip teardown) or a real cancel.
  const bulkVariationAdvancingRef = useRef(false);

  const handleBulkAddVariations = () => {
    if (selectedPosts.length === 0) return;
    const [first, ...rest] = selectedPosts;
    setBulkVariationQueue(rest);
    setBulkVariationCurrentId(first);
    setBulkVariationKey((k) => k + 1);
  };

  const handleBulkVariationSuccess = () => {
    if (bulkVariationQueue.length === 0) return;
    const [next, ...rest] = bulkVariationQueue;
    bulkVariationAdvancingRef.current = true;
    setBulkVariationQueue(rest);
    setBulkVariationCurrentId(next);
    setBulkVariationKey((k) => k + 1);
  };

  const handleBulkVariationClose = () => {
    if (bulkVariationAdvancingRef.current) {
      bulkVariationAdvancingRef.current = false;
      return;
    }
    setBulkVariationQueue([]);
    setBulkVariationCurrentId(null);
  };

  // Variations template export
  const { mutate: exportVariationsTemplate } = useExportVariationsTemplate();

  // Handler stubs for enhanced functionality
  const handleFilterByTag = (tag: string) => {
    setFilter('tags', tag);
  };

  const handleSelectVariation = (postId: number, variationId: string) => {
    devLog.log('Select variation:', postId, variationId);
    // TODO: Navigate to variation or update view
  };

  const [showAddVariationModal, setShowAddVariationModal] = useState<number | null>(null);

  const handleAddVariation = (postId: number) => {
    setShowAddVariationModal(postId);
  };

  const handleSendForReview = (postId: number) => {
    if (onSendForReview) {
      onSendForReview(postId);
    } else {
      setShowApprovalInfoDialog(true);
    }
  };

  /**
   * Handle bulk add labels
   */
  const handleBulkAddLabels = async () => {
    if (bulkLabels.length === 0 || selectedPosts.length === 0) {
      return;
    }

    setActionLoading(true);
    try {
      const result = await bulkAddLabels(bulkLabels);
      if (!result) return;
      invalidateRelease();
      setShowBulkLabelsModal(false);
      setBulkLabels([]);
      const summary = result.updatedCount > 0
        ? t('labels_added_to_posts', { count: result.updatedCount })
        : t('bulk_tags_none_updated');
      const skipped = result.skipped.length;
      const message = skipped > 0
        ? `${summary} · ${t('release_bulk_skipped', { count: skipped })}`
        : summary;
      if (skipped > 0 || result.updatedCount === 0) toast.warning(message);
      else toast.success(message);
    } catch (error) {
      devLog.error('Failed to add labels:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle bulk add hashtags
   */
  const handleBulkAddHashtags = async () => {
    if (bulkHashtags.length === 0 || selectedPosts.length === 0) {
      return;
    }

    setActionLoading(true);
    try {
      const result = await bulkAddHashtags(bulkHashtags);
      if (!result) return;
      invalidateRelease();
      setShowBulkHashtagsModal(false);
      setBulkHashtags([]);
      const summary = result.updatedCount > 0
        ? t('hashtags_added_to_posts', { count: result.updatedCount })
        : t('bulk_tags_none_updated');
      const skipped = result.skipped.length;
      const message = skipped > 0
        ? `${summary} · ${t('release_bulk_skipped', { count: skipped })}`
        : summary;
      if (skipped > 0 || result.updatedCount === 0) toast.warning(message);
      else toast.success(message);
    } catch (error) {
      devLog.error('Failed to add hashtags:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle export posts as CSV
   */
  const handleExportPosts = async () => {
    setActionLoading(true);
    try {
      await exportPosts();
    } catch (error) {
      devLog.error('Failed to export posts:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle export variations template
   */
  const handleExportVariationsTemplate = () => {
    exportVariationsTemplate(selectedPosts);
  };

  /** Summary toast for a bulk send / release without an undo snackbar, with a Details action for skipped items. */
  const announceBulkResult = (kind: 'sent' | 'released', result: BulkSendResult): void => {
    const skipped = skippedItems(result);
    const summary = buildBulkSummary(t, kind, result.counts);
    if (!summary) return;
    const action = skipped.length > 0
      ? { label: t('release_bulk_details'), onClick: () => setSkippedDetails(skipped) }
      : undefined;
    if (skipped.length > 0) toast.warning(summary, { action, duration: 10000 });
    else toast.success(summary);
  };

  /** Display name of a listed post for the skipped-items dialog */
  const skippedLabelFor = (postId: number): string | null => {
    const post = posts.find((p) => p.id === postId);
    const text = (post?.title || post?.caption || '').trim();
    return text ? (text.length > 60 ? `${text.slice(0, 60)}…` : text) : null;
  };

  /**
   * Handle bulk send for review
   */
  const handleBulkSendForReview = async () => {
    setActionLoading(true);
    try {
      const result = await approvalService.bulkSubmitForApproval(selectedPosts);
      clearSelection();
      invalidateRelease();
      if (result.counts.skipped > 0 || result.counts.done === 0) announceBulkResult('sent', result);
      announceReleaseMoment('sent', result.counts.done, result.counts.skipped === 0);
    } catch (error) {
      devLog.error('Failed to send for review:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle bulk release (approve)
   */
  const handleBulkRelease = async () => {
    setActionLoading(true);
    try {
      const result = await approvalService.bulkApprove(selectedPosts);
      const skipped = skippedItems(result);
      const summary = buildBulkSummary(t, 'released', result.counts);
      const undoShown = announceBulkRelease(result.released, {
        summary,
        onDetails: skipped.length > 0 ? () => setSkippedDetails(skipped) : undefined,
      });
      // No undo offer (nothing released, or window closed): plain summary toast instead
      if (!undoShown && (skipped.length > 0 || result.released.length === 0)) announceBulkResult('released', result);
      announceReleaseMoment('released', result.released.length, !undoShown && skipped.length === 0);
      clearSelection();
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to release posts:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle bulk mark ready
   */
  const handleBulkMarkReady = async () => {
    setActionLoading(true);
    try {
      await postsApi.bulkUpdateStatus(selectedPosts, 'READY');
      clearSelection();
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to mark posts as ready:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle bulk mark draft
   */
  const handleBulkMarkDraft = async () => {
    setActionLoading(true);
    try {
      await postsApi.bulkUpdateStatus(selectedPosts, 'DRAFT');
      clearSelection();
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to mark posts as draft:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle select all
   */
  const handleSelectAll = () => {
    if (selectedPosts.length === posts.length) {
      clearSelection();
    } else {
      selectAllPosts();
    }
  };

  /**
   * Handle duplicate
   */
  const handleDuplicate = async (postId: number) => {
    setActionLoading(true);
    try {
      await duplicatePost(postId);
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to duplicate post:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async (postId: number) => {
    setActionLoading(true);
    try {
      await deletePost(postId);
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to delete post:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle mark ready
   */
  const handleMarkReady = async (postId: number) => {
    setActionLoading(true);
    try {
      await postsApi.markReady(postId);
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to mark post as ready:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle mark draft
   */
  const handleMarkDraft = async (postId: number) => {
    setActionLoading(true);
    try {
      await postsApi.markDraft(postId);
      invalidateRelease();
    } catch (error) {
      devLog.error('Failed to mark post as draft:', error);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle bulk delete
   */
  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    setShowBulkDeleteDialog(false);

    // Send the whole selection: the server applies the release rulebook per post and answers with a
    // structured skipped[] (reason codes), so nothing is dropped silently on the client.
    const requestedIds = [...selectedPosts];
    if (requestedIds.length === 0) return;

    setActionLoading(true);
    try {
      // Call the API directly so we bypass the store's bulkDelete() (no skipped[] handling there).
      const result = await postsApi.bulkDelete(requestedIds);
      const skipped: BulkItemResult[] = (result.skipped ?? []).map((s) => ({
        postId: s.postId,
        variationId: s.variationId,
        outcome: 'skipped',
        reasonCode: s.reasonCode,
      }));
      clearSelection();
      // Refreshes the Creator list + stats (refreshIfActive) along with other surfaces
      invalidateRelease();
      const summary = buildBulkSummary(t, 'deleted', { done: result.deletedCount, skipped: skipped.length });
      if (skipped.length > 0) {
        toast.warning(summary, {
          action: { label: t('release_bulk_details'), onClick: () => setSkippedDetails(skipped) },
          duration: 10000,
        });
      } else {
        toast.success(summary || t('posts_deleted', { count: result.deletedCount, defaultValue_one: '{{count}} post deleted', defaultValue_other: '{{count}} posts deleted' }));
      }
    } catch (error) {
      devLog.error('Failed to bulk delete:', error);
      const axiosError = error as AxiosError<{ error?: string }>;
      const message =
        axiosError.response?.data?.error ||
        t('bulk_delete_error_generic', { defaultValue: "Couldn't delete posts. Please try again." });
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Handle bulk duplicate
   */
  const handleBulkDuplicate = async () => {
    setActionLoading(true);
    try {
      await bulkDuplicate();
      invalidateRelease();
      toast.success(t('posts_duplicated', { count: selectedPosts.length, defaultValue_one: '{{count}} post duplicated', defaultValue_other: '{{count}} posts duplicated' }));
    } catch (error) {
      devLog.error('Failed to bulk duplicate posts:', error);
      toast.error(t('bulk_duplicate_error', { defaultValue: 'Failed to duplicate some posts' }));
    } finally {
      setActionLoading(false);
    }
  };

  // Error state with retry button
  if (error && posts.length === 0) {
    const isRateLimited = error.toLowerCase().includes('rate limit');
    const isCircuitOpen = circuitBreakerOpen || error.includes('too many failed attempts');

    return (
      <div className="bg-white rounded-xl shadow-card p-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
          <Icon name={ICONS.CAUTION} size="lg" color="text-red-600" ariaHidden />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{t('failed_to_load_posts')}</h3>
        <p className="text-gray-600 mb-4 max-w-md mx-auto">{error}</p>

        {isRateLimited ? (
          <div className="text-sm text-gray-500 mb-4">
            <p>{t('too_many_requests')}</p>
            <p>{t('please_wait_minutes')}</p>
          </div>
        ) : isCircuitOpen ? (
          <div className="text-sm text-gray-500 mb-4">
            <p>{t('service_unavailable')}</p>
            <p>{t('please_wait_minute')}</p>
          </div>
        ) : (
          <Button variant="primary" onClick={retryFetch} className="mt-2">
            {t('try_again')}
          </Button>
        )}

        {!isRateLimited && !isCircuitOpen && (
          <p className="text-xs text-gray-400 mt-4">
            {t('contact_support')}
          </p>
        )}
      </div>
    );
  }

  // Loading state - render PostTable with isLoading to show its table skeleton
  if (isLoading && posts.length === 0) {
    return (
      <PostTable
        posts={[]}
        selectedPosts={[]}
        onSelect={() => {}}
        onSelectAll={() => {}}
        onEdit={() => {}}
        onDuplicate={() => {}}
        onDelete={() => {}}
        workflowEnabled={workflowEnabled}
        isLoading={true}
      />
    );
  }

  // Empty state
  if (!isLoading && posts.length === 0 && !error) {
    return (
      <div className="bg-white rounded-xl shadow-card p-12 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <Icon name={ICONS.LIST} size="lg" color="text-gray-400" ariaHidden />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">{t('no_posts_found')}</h3>
        <p className="text-gray-600 mb-4">
          {t('try_adjusting_filters')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message (when posts are already loaded) */}
      {error && posts.length > 0 && (
        <div
          className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg"
          role="alert"
        >
          <Icon name={ICONS.CAUTION} size="sm" color="text-red-600 flex-shrink-0 mt-0.5" ariaHidden />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900 mb-1">{t('failed_to_refresh_posts')}</p>
            <p className="text-sm text-red-600">{error}</p>
          </div>
          <div className="flex gap-2">
            {!circuitBreakerOpen && !error.toLowerCase().includes('rate limit') && (
              <Button variant="secondary" size="sm" onClick={retryFetch}>
                {t('retry')}
              </Button>
            )}
            <button
              onClick={clearError}
              className="text-red-600 hover:text-red-700 font-medium text-sm px-2"
            >
              {t('dismiss')}
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions Toolbar - Show when posts are selected */}
      {selectedPosts.length > 0 && (
        <div id="bulk-actions-bar" className="sticky top-[calc(4.1rem+59px)] z-20 flex flex-col gap-3 p-3 sm:p-4 bg-soft-mint border border-light-teal-gray rounded-lg shadow-md">
          {/* Row 1: Selection count + Clear */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name={ICONS.CHECK_CIRCLE} size="sm" color="text-dark-teal" ariaHidden />
              <span className="text-sm font-medium text-dark-teal">
                {t('posts_selected', { count: selectedPosts.length })}
              </span>
            </div>
            <button
              onClick={clearSelection}
              disabled={actionLoading}
              className="p-1.5 hover:bg-gray-200/50 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
              aria-label={t('clear_selection')}
            >
              <Icon name={ICONS.CROSS} size="sm" ariaHidden />
            </button>
          </div>

          {/* Row 2: Actions — 2-col grid on mobile, inline on sm+ */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
            {!permissions.workflowEnabled && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleBulkMarkReady}
                disabled={actionLoading}
                className="flex items-center justify-center gap-1.5 text-xs sm:text-sm"
              >
                <Icon name={ICONS.CHECK_CIRCLE} size="sm" ariaHidden />
                {t('mark_ready')}
              </Button>
            )}
            {!permissions.workflowEnabled && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkMarkDraft}
                disabled={actionLoading}
                className="flex items-center justify-center gap-1.5 text-xs sm:text-sm"
              >
                <Icon name={ICONS.DRAFT} size="sm" ariaHidden />
                {t('mark_draft')}
              </Button>
            )}
            {canBulkDelete && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleBulkDelete}
                disabled={actionLoading}
                className="flex items-center justify-center gap-1.5 text-xs sm:text-sm"
              >
                <Icon name={ICONS.TRASH} size="sm" ariaHidden />
                {t('delete')}
              </Button>
            )}

            <DropdownMenu
              label={t('more')}
              align="left"
              disabled={actionLoading}
              className="w-full sm:w-auto text-xs sm:text-sm"
              items={[
                ...(canBulkDuplicate
                  ? [
                      {
                        label: t('duplicate', { defaultValue: 'Duplicate' }),
                        icon: ICONS.COPY,
                        onClick: handleBulkDuplicate,
                      },
                    ]
                  : []),
                ...(canBulkEdit
                  ? [
                      {
                        label: t('add_tags'),
                        icon: ICONS.TAG,
                        onClick: () => setShowBulkLabelsModal(true),
                      },
                      {
                        label: t('add_hashtags'),
                        customIcon: <FontAwesomeIcon icon={faHashtag} className="w-4 h-4" />,
                        onClick: () => setShowBulkHashtagsModal(true),
                      },
                    ]
                  : []),
                ...(canCreateVariation
                  ? [
                      {
                        label: t('add_variation', { defaultValue: 'Add Variation' }),
                        icon: ICONS.PLUS_CIRCLE,
                        onClick: handleBulkAddVariations,
                      },
                      {
                        label: t('export_for_variations'),
                        icon: ICONS.DOWNLOAD,
                        onClick: handleExportVariationsTemplate,
                      },
                    ]
                  : []),
                {
                  label: t('export'),
                  icon: ICONS.DOWNLOAD,
                  onClick: handleExportPosts,
                },
                {
                  label: t('send_for_review'),
                  icon: ICONS.PAPER_PLANE,
                  onClick: handleBulkSendForReview,
                  show: permissions.workflowEnabled && canSendForReview,
                },
                {
                  label: t('release'),
                  icon: ICONS.CHECK,
                  onClick: handleBulkRelease,
                  show: permissions.workflowEnabled && canReleasePosts,
                },
              ]}
            />
          </div>
        </div>
      )}

      {/* Posts Table */}
      <PostTable
        posts={posts}
        selectedPosts={selectedPosts}
        onSelect={selectPost}
        onSelectAll={handleSelectAll}
        onEdit={onEdit || (() => {})}
        onDuplicate={handleDuplicate}
        onDelete={handleDelete}
        onFilterByTag={handleFilterByTag}
        onSelectVariation={handleSelectVariation}
        onAddVariation={canCreateVariation ? handleAddVariation : undefined}
        onScheduleNow={onScheduleNow}
        onSendForReview={handleSendForReview}
        onMarkReady={handleMarkReady}
        onMarkDraft={handleMarkDraft}
        workflowEnabled={workflowEnabled}
        carteBlanche={carteBlanche}
        isLoading={isLoading}
        isAgencyTeam={isAgencyTeam}
        canEditPost={permissions.canEditPost}
        canDeletePost={permissions.canDeletePost}
        canSendForReview={permissions.canSendForReview}
        canMarkReady={permissions.canMarkReady}
        canMarkDraft={permissions.canMarkDraft}
        canSchedulePost={permissions.canSchedulePost}
        canDuplicatePost={permissions.canDuplicatePost}
      />

      {/* Load More */}
      {(hasMore || pagination.total > posts.length) && (
        <div className="flex flex-col items-center gap-2 pt-6 border-t border-gray-200">
          <p className="text-xs sm:text-sm text-gray-600">
            {t('showing_x_of_y_posts', { shown: posts.length, total: pagination.total })}
          </p>
          <Button
            variant="secondary"
            onClick={loadMore}
            disabled={isLoading}
            loading={isLoading}
          >
            {t('load_more')}
          </Button>
        </div>
      )}

      {/* Bulk Add Labels Modal */}
      <Modal
        open={showBulkLabelsModal}
        onOpenChange={(open) => !open && setShowBulkLabelsModal(false)}
        title={t('add_labels_to_posts', { count: selectedPosts.length })}
        showCloseButton={false}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('labels_description')}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('labels')}
            </label>
            <LabelInput
              labels={bulkLabels}
              onChange={setBulkLabels}
              disabled={actionLoading}
              maxLabels={20}
            />
          </div>

          <div className="flex gap-1.5 sm:gap-2 pt-4 justify-end">
            <CancelButton onClick={() => setShowBulkLabelsModal(false)} disabled={actionLoading} />
            <Button
              variant="primary"
              onClick={handleBulkAddLabels}
              disabled={bulkLabels.length === 0 || actionLoading}
              loading={actionLoading}
            >
              {t('add_tags')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Add Hashtags Modal */}
      <Modal
        open={showBulkHashtagsModal}
        onOpenChange={(open) => !open && setShowBulkHashtagsModal(false)}
        title={t('add_hashtags_to_posts', { count: selectedPosts.length })}
        showCloseButton={false}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t('hashtags_description')}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('hashtags')}
            </label>
            <Input
              type="text"
              value={bulkHashtags.join(', ')}
              onChange={(e) => {
                const value = e.target.value;
                if (value.trim() === '') {
                  setBulkHashtags([]);
                } else {
                  setBulkHashtags(value.split(',').map(h => h.trim()).filter(Boolean));
                }
              }}
              placeholder={t('hashtags_placeholder')}
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('hashtags_auto_hash')}
            </p>
          </div>

          {/* AI Hashtag Generator Button with FontAwesome icon */}
          <div className="border-t border-gray-200 pt-4 flex justify-end">
            <Button
              variant="outline"
              onClick={() => setShowHashtagGenerator(true)}
              className="flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faHashtag} className="w-4 h-4" />
              {t('generate_hashtags_ai')}
            </Button>
          </div>

          <div className="flex gap-1.5 sm:gap-2 pt-4 justify-end">
            <CancelButton onClick={() => setShowBulkHashtagsModal(false)} disabled={actionLoading} />
            <Button
              variant="primary"
              onClick={handleBulkAddHashtags}
              disabled={bulkHashtags.length === 0 || actionLoading}
              loading={actionLoading}
            >
              {t('add_hashtags')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Hashtag Generator Modal (nested) */}
      <HashtagGenerator
        open={showHashtagGenerator}
        onClose={() => setShowHashtagGenerator(false)}
        onHashtagsAdded={(hashtags) => {
          // Merge with existing hashtags (remove # prefix as bulkHashtags stores without #)
          const cleanedHashtags = hashtags.map(h => h.replace(/^#/, ''));
          setBulkHashtags(prev => [...new Set([...prev, ...cleanedHashtags])]);
          setShowHashtagGenerator(false);
        }}
        nested={true}
      />

      {/* Add Variation Modal */}
      {showAddVariationModal && (
        <Modal
          open={true}
          onOpenChange={(open) => !open && setShowAddVariationModal(null)}
          title={t('create_new_version')}
          size="md"
          showCloseButton={false}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('version_description')}
            </p>

            <div className="bg-soft-mint border border-light-teal-gray rounded-lg p-3">
              <p className="text-sm text-gray-900">
                <strong>{t('post_list_tip_label')}</strong> {t('version_tip')}
              </p>
            </div>

            <div className="flex gap-1.5 sm:gap-2 pt-4 justify-end">
              <CancelButton onClick={() => setShowAddVariationModal(null)} disabled={actionLoading} />
              <Button
                variant="primary"
                onClick={async () => {
                  const postId = showAddVariationModal;
                  setShowAddVariationModal(null);
                  setActionLoading(true);
                  try {
                    const duplicated = await postsApi.duplicate(postId);
                    // Open the duplicated post in the editor
                    if (onEdit && duplicated?.id) {
                      onEdit(duplicated.id);
                    }
                  } catch (error) {
                    devLog.error('Failed to create version:', error);
                    setShowVersionErrorDialog(true);
                  } finally {
                    setActionLoading(false);
                  }
                }}
                disabled={actionLoading}
                loading={actionLoading}
              >
                {t('create_edit_version')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      <BulkSkippedDialog items={skippedDetails} onClose={() => setSkippedDetails(null)} labelFor={skippedLabelFor} />

      {/* Approval Info Dialog */}
      <ConfirmDialogSimple
        open={showApprovalInfoDialog}
        onOpenChange={setShowApprovalInfoDialog}
        title={t('coming_soon')}
        description={t('approval_coming_soon')}
        confirmText={t('got_it')}
        variant="default"
        onConfirm={() => setShowApprovalInfoDialog(false)}
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        open={showBulkDeleteDialog}
        onOpenChange={setShowBulkDeleteDialog}
        title={t('delete_posts')}
        description={t('delete_posts_confirm', { count: selectedPosts.length })}
        confirmText={t('delete')}
        cancelText={t('cancel')}
        variant="danger"
        onConfirm={confirmBulkDelete}
        loading={actionLoading}
      />

      {/* Date Required Dialog */}
      <ConfirmDialogSimple
        open={showDateRequiredDialog}
        onOpenChange={setShowDateRequiredDialog}
        title={t('date_required')}
        description={t('select_date_before_reschedule')}
        confirmText={t('ok')}
        variant="warning"
        onConfirm={() => setShowDateRequiredDialog(false)}
      />

      {/* Version Error Dialog */}
      <ConfirmDialogSimple
        open={showVersionErrorDialog}
        onOpenChange={setShowVersionErrorDialog}
        title={t('error')}
        description={t('version_error')}
        confirmText={t('ok')}
        variant="danger"
        onConfirm={() => setShowVersionErrorDialog(false)}
      />

      {/* Bulk Variation Creator — opens once per selected post in sequence */}
      <VariationCreator
        key={bulkVariationKey}
        isOpen={bulkVariationCurrentId !== null}
        preSelectedPostId={bulkVariationCurrentId ?? undefined}
        onClose={handleBulkVariationClose}
        onSuccess={handleBulkVariationSuccess}
      />

    </div>
  );
};
